import React from 'react';
import StopMarker from '../../partial/StopMarker';
import ServiceItemsList from './ServiceItemsList';
import { STOP_DISPLAY_STATES, STOP_STATE_VISUAL, deriveStopDisplayState } from '../../../util/routeStopState';
import openNavigation from '../../../util/openNavigation';

// One stop in the mobile route list. Two states:
//
//   Collapsed (default) — state-coded header, customer + vehicle + services
//   one-liner, address, Call/Directions row, state-appropriate primary action.
//
//   Expanded — same header strip stays at the top; the body expands to show
//   each service's full description with checkable steps (ServiceItemsList,
//   shared with shop ServiceCard). Primary action is anchored at the bottom.
//
// Expansion is owned by the parent (RouteView) so it can stay exclusive —
// only one card open at a time. Tap the card body to toggle. Interactive
// elements inside the card (Call, Directions, primary CTA, item checkboxes)
// stopPropagation so they don't collapse the card.
//
// Props:
//   stop                — server stop object (see routeService JSDoc for shape)
//   expanded            — boolean, whether this card is the expanded one
//   onToggleExpand(stop) — fires when the card body is tapped
//   onFinishStop(stop)  — primary CTA in IN_PROGRESS / ARRIVED states
//   onStartTravel(stop) — explicit "Start Travel" chip (geo-denied fallback)
//   onArrivedManually(stop) — "I've arrived" chip (geofence didn't fire)
//   onToggleItem(item)  — work-step checkbox toggled (RouteView reloads after)
//   currentUserId       — for ServiceItemsList ownership styling

const formatTimeRange = (start, end) => {
  if (!start && !end) return '';
  if (!end) return start;
  return `${start} — ${end}`;
};

const serviceHeading = (svc) => {
  const category = svc.service_category || svc.category;
  const desc = svc.custom_description || svc.description || svc.name;
  if (category && desc) return `${category} - ${desc}`;
  return category || desc || '';
};

const summarizeServices = (services) => {
  if (!Array.isArray(services) || services.length === 0) return '';
  if (services.length === 1) return serviceHeading(services[0]);
  return services.map(serviceHeading).filter(Boolean).join(' · ');
};

const StopCard = ({
  stop,
  expanded = false,
  onToggleExpand,
  onStartTravel,
  onCheckIn,
  onStartService,
  onFinishStop,
  onToggleItem,
  currentUserId,
}) => {
  if (!stop) return null;

  const displayState = deriveStopDisplayState(stop.state);
  const visual = STOP_STATE_VISUAL[displayState];
  const isCompleted = displayState === STOP_DISPLAY_STATES.COMPLETED;
  const isInProgress = displayState === STOP_DISPLAY_STATES.IN_PROGRESS;
  const isArrived = displayState === STOP_DISPLAY_STATES.ARRIVED;
  const isInTravel = displayState === STOP_DISPLAY_STATES.IN_TRAVEL;
  const isUpNext = displayState === STOP_DISPLAY_STATES.UP_NEXT;

  const customer = stop.customer || {};
  const customerName = customer.name || 'Customer';
  const customerPhone = customer.phone;
  const address = customer.address || '';
  const services = Array.isArray(stop.services) ? stop.services : [];

  const handleDirections = (e) => {
    if (e) e.stopPropagation();
    openNavigation({
      address,
      lat: customer.lat,
      lng: customer.lng,
    });
  };

  const handleCardClick = () => {
    if (onToggleExpand) onToggleExpand(stop);
  };

  // Helper so interactive elements don't bubble up and collapse the card.
  const stop_ = (handler) => (e) => {
    e.stopPropagation();
    if (handler) handler(stop);
  };

  return (
    <div
      className={`stop-card stop-card--${displayState}`}
      onClick={handleCardClick}
      style={{
        background: '#ffffff',
        border: `1px solid ${expanded ? visual.color : '#e5e7eb'}`,
        borderLeft: `4px solid ${visual.color}`,
        borderRadius: 8,
        padding: 12,
        marginBottom: 4,
        opacity: isCompleted ? 0.65 : 1,
        cursor: 'pointer',
      }}
    >
      {/* Header — state badge + time range + expand chevron */}
      <div className="d-flex align-items-center mb-2" style={{ gap: 12 }}>
        <StopMarker
          sequence={stop.sequence}
          displayState={displayState}
          active={isInProgress || isArrived}
          size="sm"
        />
        <span
          className="stop-card__state-pill"
          style={{
            background: `${visual.color}1a`,
            color: visual.color,
            padding: '2px 8px',
            borderRadius: 12,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.5,
          }}
        >
          <i className={`${visual.icon} mr-1`} aria-hidden="true" />
          {visual.label}
        </span>
        <span className="text-muted" style={{ fontSize: 13 }}>
          {formatTimeRange(stop.start_time, stop.end_time)}
        </span>
        <span className="ml-auto text-muted" style={{ fontSize: 13 }}>
          <i className={`fa-sharp fa-regular fa-chevron-${expanded ? 'up' : 'down'}`} aria-hidden="true" />
        </span>
      </div>

      {/* Body — customer / vehicle / services / address */}
      <div className="mb-2">
        <div style={{ fontWeight: 600 }}>{customerName}</div>
        {!expanded && stop.vehicle_label && (
          <div className="text-muted" style={{ fontSize: 13 }}>{stop.vehicle_label}</div>
        )}
        {expanded && (() => {
          // Expanded view shows the full year/make/model/trim breakdown.
          // Server may or may not flatten these onto the stop — fall back to
          // vehicle_label so we never blank the line entirely.
          const ymmt = [stop.year || stop.Year, stop.make || stop.Make, stop.model || stop.Model, stop.trim || stop.Trim]
            .filter(Boolean).join(' ');
          const color = stop.color || stop.Color;
          const vin = stop.vin || stop.VIN;
          const line = ymmt || stop.vehicle_label;
          if (!line && !color && !vin) return null;
          return (
            <div className="text-muted" style={{ fontSize: 13 }}>
              {color && <span className="mr-1">{color}</span>}
              {line || '—'}
              {vin && <span className="ml-1 font-monospace small">· {vin}</span>}
            </div>
          );
        })()}
        {!expanded && (
          <div style={{ fontSize: 14, marginTop: 4 }}>{summarizeServices(services)}</div>
        )}
        {address && (
          <div className="text-muted" style={{ fontSize: 13, marginTop: 4 }}>
            <i className="fa-sharp fa-regular fa-location-dot mr-1" aria-hidden="true" />
            {address}
          </div>
        )}
      </div>

      {/* Expanded body — each service with its step checklist */}
      {expanded && services.length > 0 && (
        <div
          className="pt-2 mb-2"
          style={{ borderTop: '1px solid #f3f4f6' }}
          onClick={(e) => e.stopPropagation()}
        >
          {services.map((svc, idx) => (
            <div key={svc.request_service_id || idx} className="mb-3">
              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                {serviceHeading(svc) || `Service ${idx + 1}`}
              </div>
              <ServiceItemsList
                items={svc.items || []}
                currentUserId={currentUserId}
                serviceState={svc.service_state}
                totalTaskCount={svc.tech_assignment?.total_task_count || (svc.items || []).length}
                onToggleItem={onToggleItem}
              />
            </div>
          ))}
        </div>
      )}

      {/* Primary action — state-aware. Mirrors shop-tech flow:
            ARRIVED → Start Service → IN_PROGRESS → Complete Service.
          Tech's check-in (Phase I) writes current_state='received' so
          ARRIVED here is effectively "checked-in, ready to start". */}
      {isInProgress && (
        <button
          type="button"
          className="btn btn-primary btn-block"
          onClick={stop_(onFinishStop)}
        >
          <i className="fa-sharp fa-regular fa-check mr-2" aria-hidden="true" />
          Complete Service
        </button>
      )}

      {isArrived && (
        <button
          type="button"
          className="btn btn-success btn-block"
          onClick={stop_(onStartService)}
        >
          <i className="fa-sharp fa-regular fa-play mr-2" aria-hidden="true" />
          Start Service
        </button>
      )}

      {isInTravel && (
        <button
          type="button"
          className="btn btn-outline-primary btn-block"
          onClick={stop_(onCheckIn)}
        >
          <i className="fa-sharp fa-regular fa-play mr-2" aria-hidden="true" />
          Start
        </button>
      )}

      {isUpNext && (
        <button
          type="button"
          className="btn btn-outline-secondary btn-block btn-sm"
          onClick={stop_(onStartTravel)}
        >
          <i className="fa-sharp fa-regular fa-car mr-2" aria-hidden="true" />
          Start Travel
        </button>
      )}

      {/* Secondary actions */}
      <div
        className="d-flex justify-content-around mt-2 pt-2"
        style={{ borderTop: '1px solid #f3f4f6' }}
        onClick={(e) => e.stopPropagation()}
      >
        {customerPhone && (
          <a className="btn btn-link btn-sm text-decoration-none" href={`tel:${customerPhone}`}>
            <i className="fa-sharp fa-regular fa-phone mr-1" aria-hidden="true" />
            Call
          </a>
        )}
        {address && !isInProgress && !isCompleted && (
          <button type="button" className="btn btn-link btn-sm text-decoration-none" onClick={handleDirections}>
            <i className="fa-sharp fa-regular fa-diamond-turn-right mr-1" aria-hidden="true" />
            Directions
          </button>
        )}
      </div>
    </div>
  );
};

export default StopCard;
